import React, { useEffect, useState } from "react";
import { Button, Form, Table, Badge } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { BsSearch, BsArrowLeft } from "react-icons/bs";
import CurrencyFlag from "react-currency-flags";
import codes from "currency-codes";
import getSymbolFromCurrency from "currency-symbol-map"; // for symbols
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import { toast } from "sonner";
import { customStyles } from "../../../Utils/helpers/commonhelper";
import masterImage from '../../../Utils/assets/img/masterImage.png'

//  Validation Schema
const schema = z.object({
  currency: z.object(
    {
      value: z.string(),
      label: z.string(),
      symbol: z.string().optional(),
      number: z.string().optional(),
    },
    { required_error: "Please select a currency" }
  ),
});

//  Dynamic currency options with symbol
const currencyOptions = codes.codes().map((code) => {
  const c = codes.code(code);
  return {
    value: code,
    label: c.currency,
    symbol: getSymbolFromCurrency(code) || "-",
    number: c.number,
  };
});

const baseCurrency = { value: "USD", label: "United States Dollar (USD)" };

//  Custom Option Renderer
const customOption = (props) => {
  const { innerProps, data } = props;
  return (
    <div {...innerProps} className="d-flex align-items-center p-2 ">
      <CurrencyFlag currency={data.value} size="sm" className="me-2" />
      <span>
        {data.label} ({data.value})
      </span>
    </div>
  );
};

//  Custom Single Value Renderer
const customSingleValue = ({ data }) => (
  <div className="d-flex align-items-center top_margin ">
    <style jsx>{`
      .top_margin {
        margin-top : -1rem !important;
      }
    `}</style>
    <CurrencyFlag currency={data.value} size="sm" className="me-2" />
    <span>
      {data.label} ({data.value})
    </span>
  </div>
);

export const AddCurrency = () => {
  // Session storage for customer token
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customerId = customer?.customer_details?.customer_id;

  // Auth tokens and headers
  const navigate = useNavigate();
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const extras = { customer, setCustomer, rf, navigate };

  const headers = {
    authentication: rf,
    Authorization: token,
  };

  const [currencyData, setCurrencyData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { currency: null },
  });

  useEffect(() => {
    fetchCurrencyTable();
  }, []);

  // master permissions 
  const permission = customer.customer_details?.permission;
  const user_type = Number(customer.customer_details?.user_type)
  const settingsPermission = permission?.filter(c => c.module_name === "setting")
  // view permission for master 
  const masterGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.delete)) : true,
  }

  const fetchCurrencyTable = async () => {
    try {
      const res = await GetRequest(
        `masters/fetchCurrency/${customerId}`,
        { headers },
        extras
      );

      if (res.status !== "success") {
        toast.error(res.message || "Failed to fetch currency");
        return;
      }

      // Parse currency field
      const formattedData = res.data.map((item) => {
        let parsedCurrency = {};
        try {
          parsedCurrency = JSON.parse(item.currency); // parse string to object
        } catch (e) {
          console.error("Failed to parse currency", e);
        }

        return {
          id: item.id,
          customer_id: item.customer_id,
          status: item.status,
          code: parsedCurrency.value,
          name: parsedCurrency.label,
          symbol: parsedCurrency.symbol,
          number: parsedCurrency.number,
          added_by: item.added_by,
          created_at: item.created_at,
        };
      });

      setCurrencyData(formattedData); //set parsed data
    } catch (error) {
      console.error(error);
    }
  };


  const onSubmit = async (data) => {
    if (!masterGrant?.isCreate) {
      toast.warning("You are not authorized for this action!")
      return
    }
    try {
      const payload = {
        customer_id: customerId,
        currency: data.currency,
        user_login_id: customer?.customer_details?.id,
      };
      const res = await PostRequest(
        `/masters/createCurrency
  `,
        payload,
        false,
        { headers },
        extras
      );
      if (res.status === "success") {
        toast.success(res.message || "currency is add successfully");
        reset({ currency: null });
        await fetchCurrencyTable();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditClick = async (c) => {
    try {
      const payload = {
        customer_id: customerId,
      };
      const res = await PutRequest(
        `masters/updateCurrency/${c.id}/${customer?.customer_details?.id}`,
        payload,
        { headers },
        extras
      );
      if (res.status === "fail" || res.status !== "success") {
        toast.error(res.message);
        return;
      }
      if (res.status === "success") {
        toast.success(res.message || "updated succefully ..");
        await fetchCurrencyTable();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // const onDelete = (code) => {
  //   setCurrencyList(currencyList.filter((c) => c.code !== code));
  // };

  return (
    <div className="container-fluid p-3 currency-container mb-5">
      <style jsx> {`
        .currency-container{
          background: #f8f9fa;
          color: #343a40;
        }
      `}</style>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Add Multiple Currencies</h5>
          <p className="text-muted small">
            Select currencies and manage their exchange rates for your account.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button className="btn btn-default-outline" onClick={() => navigate(-1)}>
            <BsArrowLeft className="me-1 " /> Back
          </Button>
        </div>
      </div>

      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />

      <div className="row">
        {/* Form Section */}
        <div className="col-md-4 mb-4">
          <div className="p-3 border rounded bg-white shadow-sm">
            <div className="mb-3">
              <h6 className="fw-semibold mb-1">Add Currency</h6>
              <p className="text-muted small">Select a currency to add.</p>
            </div>

            <Form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                <Form.Label>Base Currency</Form.Label>
                <Form.Control value={baseCurrency.label} disabled />
              </Form.Group>

              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Label>
                        Select Currency <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        styles={customStyles}
                        options={currencyOptions}
                        placeholder="Choose currency"
                        value={field.value}
                        isClearable="true"
                        onChange={(selected) => field.onChange(selected)}
                        components={{
                          Option: customOption,
                          SingleValue: customSingleValue,
                        }}
                      // classNamePrefix="react-select"
                      />
                    </>
                  )}
                />
                {errors.currency && (
                  <small className="text-danger">
                    {errors.currency.message}
                  </small>
                )}
              </Form.Group>

              <Button type="submit" className="w-100 btn btn-default">
                Add Currency
              </Button>
            </Form>

            <div className="text-center mt-4">
              <img
                src={masterImage}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-0">Currency</h6>
                <p className="text-muted  mb-0 small ">
                  Define and manage employee designations within an
                  organization. This feature typically includes options to add,
                  edit, and delete designations for better workforce
                  organization.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="col-md-8">
          <div className="p-3 border rounded bg-white shadow-sm">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h6 className="fw-semibold mb-1">Currencies List</h6>
                <p className="text-muted small mb-0">
                  List of all active currencies in your account.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 border rounded px-2">
                <Form.Control
                  type="search"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    maxWidth: "200px",
                    border: "none",
                    boxShadow: "none",
                  }}
                />
                <BsSearch style={{ width: "20px", height: "20px" }} />

              </div> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  class="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>

            <Table bordered hover responsive className="align-middle mt-3">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th style={{ width: "70px" }}>S.No</th>
                  <th>Currency</th>
                  <th style={{ width: "120px" }}>Symbol</th>
                  <th style={{ width: "120px" }}>Status</th>
                  <th style={{ width: "150px" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {currencyData
                  .filter((c) =>
                    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((c, idx) => (
                    <tr key={c.id}>
                      <td className="text-center">{idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <CurrencyFlag currency={c.code} size="sm" />
                          {c.name} ({c.code})
                        </div>
                      </td>
                      <td className="text-center">{c.symbol || "-"}</td>
                      <td className="text-center">
                        <Badge
                          bg={c.status === "Active" ? "success" : "danger"}
                          pill
                        >
                          {c.status}
                        </Badge>
                      </td>
                      <td className="text-center">

                        <Button
                          size="sm"
                          variant="outline-primary"
                          title="Deactive"
                          onClick={() => {
                            if (!masterGrant?.isEdit) {
                              toast.warning("You are not authorized for this action!")
                              return
                            }
                            handleEditClick(c);
                          }}
                        >
                          {c.status === "Active" ? "Deactivate" : "Acitvate"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                {currencyData.filter((c) =>
                  c.name?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No currencies found !!
                      </td>
                    </tr>
                  )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div >
  );
};
